import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { searchPatientAppointments } from "../../Redux/actions/doctorActions/doctorActions.js";
import ViewEditRecordModal from "./ViewEditRecordModal.jsx";
import Loading from "../Loading.jsx";
import { useSanitizedInput } from "../../hooks/useSanitizedInput.js";
import "./DoctorModals.css";
import { convertTo12Hour } from "../../utils/timeUtils";

// ✅ Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
};

const CompletedAppointmentsHistory = ({ onClose }) => {
  const dispatch = useDispatch();
  
  // ✅ CHANGED: Use searchedAppointments instead of completedAppointments
  const { 
    searchedAppointments = [], 
    searchedAppointmentsLoading 
  } = useSelector((state) => state.doctor || {});

  // ✅ NEW: Patient search state
  const [searchQuery, handleSearchChange, resetSearch, setSearchQuery] = useSanitizedInput("", "text");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // ✅ NEW: Debounced search function
  const doSearch = useCallback(
    debounce((query) => {
      if (query && query.trim().length >= 2) {
        dispatch(searchPatientAppointments(query.trim()));
        setShowSearchResults(true);
      }
    }, 500),
    [dispatch]
  );

  // ✅ NEW: Trigger search on query change
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      doSearch(searchQuery);
    } else {
      setShowSearchResults(false);
    }

    return () => {
      doSearch.cancel();
    };
  }, [searchQuery, doSearch]);

  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowEditModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString, time) => {
    const date = formatDate(dateString);
    const time12 = convertTo12Hour(time);
    return `${date} at ${time12}`;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-xl">
        <div className="modal-header-custom">
          <h4>
            <i className="bi bi-clock-history me-2"></i>
            Completed Appointments History
          </h4>
          <button className="btn-close-custom" onClick={onClose}>
            <i className="bi bi-x-lg">Close</i>
          </button>
        </div>

        <div className="modal-body-custom">
          {/* ✅ NEW: Patient Search Section */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-12">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-search me-2"></i>
                    Search Patient by Name or MR Code
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type patient name or MR code (min 2 characters)..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Enter at least 2 characters to search for patient records
                  </small>
                </div>

                {searchQuery.trim() && (
                  <div className="col-md-12">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        resetSearch();
                        setShowSearchResults(false);
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear Search
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ✅ NEW: Search Instructions (shown when no search) */}
          {!searchQuery.trim() && (
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Search for patient records:</strong>
              <ul className="mb-0 mt-2">
                <li>Enter patient name or MR code in the search box above</li>
                <li>All completed and on-hold appointments for the patient will be displayed</li>
                <li>Records are sorted by date (most recent first)</li>
                <li>Click "View/Edit" to see or modify medical records</li>
              </ul>
            </div>
          )}

          {/* ✅ Loading State */}
          {searchedAppointmentsLoading && (
            <div className="text-center py-5">
              <Loading />
              <p className="mt-3 text-muted">Searching appointments...</p>
            </div>
          )}

          {/* ✅ Search Results */}
          {showSearchResults && !searchedAppointmentsLoading && (
            <>
              {searchedAppointments.length === 0 ? (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  No appointments found for "{searchQuery}"
                  <br />
                  <small className="text-muted">
                    Try searching with a different name or MR code
                  </small>
                </div>
              ) : (
                <>
                  <div className="alert alert-success mb-3">
                    <i className="bi bi-check-circle me-2"></i>
                    <strong>Found {searchedAppointments.length} appointment(s)</strong> for "{searchQuery}"
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "180px" }}>
                            <i className="bi bi-calendar3 me-1"></i>
                            Date & Time
                          </th>
                          <th style={{ width: "120px" }}>
                            <i className="bi bi-hash me-1"></i>
                            MR Code
                          </th>
                          <th>
                            <i className="bi bi-person me-1"></i>
                            Patient Info
                          </th>
                          <th style={{ width: "100px" }}>
                            <i className="bi bi-circle-fill me-1"></i>
                            Status
                          </th>
                          <th style={{ width: "150px" }}>
                            <i className="bi bi-building me-1"></i>
                            Department
                          </th>
                          <th style={{ width: "120px" }} className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchedAppointments.map((appt) => (
                          <tr key={appt._id}>
                            <td>
                              <div>
                                <strong>{appt.date}</strong>
                                <br />
                                <small className="text-muted">{convertTo12Hour(appt.time)}</small>
                              </div>
                            </td>
                            <td>
                              <code className="bg-light px-2 py-1 rounded">
                                {appt.mrCode}
                              </code>
                            </td>
                            <td>
                              <div>
                                <strong>{appt.pName}</strong>
                                <br />
                                {appt.fatherName && (
                                  <small className="text-muted">
                                    S/O: {appt.fatherName}
                                  </small>
                                )}
                                <br />
                                <small className="text-muted">
                                  Age: {appt.age || 'N/A'}
                                </small>
                              </div>
                            </td>
                            <td>
                              {appt.status === "completed" && (
                                <span className="badge bg-success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Completed
                                </span>
                              )}
                              {appt.status === "on-hold" && (
                                <span className="badge bg-warning">
                                  <i className="bi bi-hourglass-split me-1"></i>
                                  On Hold
                                </span>
                              )}
                            </td>
                            <td>
                              <small>{appt.department}</small>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleEditAppointment(appt)}
                                title="View or edit medical record"
                              >
                                <i className="bi bi-pencil-square me-1"></i>
                                View/Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer-custom">
          <button className="btn btn-secondary" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i>
            Close
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedAppointment && (
        <ViewEditRecordModal
          appointment={selectedAppointment}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAppointment(null);
            // Re-search to refresh the list
            if (searchQuery.trim().length >= 2) {
              dispatch(searchPatientAppointments(searchQuery.trim()));
            }
          }}
        />
      )}
    </div>
  );
};

export default CompletedAppointmentsHistory;